import { createContext, Dispatch, FC, PropsWithChildren, Reducer, useReducer } from 'react';

import {
  AddressFieldInput,
  CheckboxFieldInput,
  EmailFieldInput,
  NameFieldInput,
} from '../../../__generated__/graphql';

interface BaseAction {
  id: number;
}

// Field value types
export interface AddressFieldValue extends BaseAction {
  addressValues: AddressFieldInput;
}

export interface CaptchaFieldValue extends BaseAction {
  value: string;
}

export interface CheckboxFieldValue extends BaseAction {
  checkboxValues: CheckboxFieldInput[];
}

export interface EmailFieldValue extends BaseAction {
  emailValues: EmailFieldInput;
}

export interface HiddenFieldValue extends BaseAction {
  value: string;
}

export interface NameFieldValue extends BaseAction {
  nameValues: NameFieldInput;
}

export interface NumberFieldValue extends BaseAction {
  value: string;
}
export interface StringFieldValue extends BaseAction {
  value: string;
}

interface StringFieldValues extends BaseAction {
  values: string[];
}

// Errors
interface UpdateErrorsPayload {
  errors: GravityFormsState['errors'];
}

export interface GravityFormsStateFields {
  addressFields: Record<number, AddressFieldValue>;
  captchaFields: Record<number, CaptchaFieldValue>;
  checkboxFields: Record<number, CheckboxFieldValue>;
  dateFields: Record<number, StringFieldValue>;
  emailFields: Record<number, EmailFieldValue>;
  hiddenFields: Record<number, HiddenFieldValue>;
  multiSelectFields: Record<number, StringFieldValues>;
  nameFields: Record<number, NameFieldValue>;
  numberFields: Record<number, NumberFieldValue>;
  phoneFields: Record<number, StringFieldValue>;
  radioFields: Record<number, StringFieldValue>;
  selectFields: Record<number, StringFieldValue>;
  textAreaFields: Record<number, StringFieldValue>;
  textFields: Record<number, StringFieldValue>;
  timeFields: Record<number, StringFieldValue>;
  websiteFields: Record<number, StringFieldValue>;
}

export interface GravityFormsState {
  errors: Record<number, null | string[]>;
  fields: GravityFormsStateFields;
  initialized: boolean;
}

export type GravityFormsStateProperty = GravityFormsStateFields[keyof GravityFormsStateFields];
export type GravityFormsStateValue = GravityFormsStateProperty[number];

// Action types
interface InitializeFieldsAction {
  payload: {
    state: GravityFormsState;
  };
  type: 'initializeFields';
}
interface UpdateAddressAction {
  payload: AddressFieldValue;
  type: 'updateAddressFieldValue';
}
interface UpdateCaptchaFieldValueAction {
  payload: CaptchaFieldValue;
  type: 'updateCaptchaFieldValue';
}
interface UpdateCheckboxAction {
  payload: CheckboxFieldValue;
  type: 'updateCheckboxFieldValue';
}
interface UpdateDateFieldValueAction {
  payload: StringFieldValue;
  type: 'updateDateFieldValue';
}
interface UpdateEmailAction {
  payload: EmailFieldValue;
  type: 'updateEmailFieldValue';
}
interface UpdateHiddenAction {
  payload: HiddenFieldValue;
  type: 'updateHiddenFieldValue';
}
interface UpdateErrorsAction {
  payload: UpdateErrorsPayload;
  type: 'updateErrors';
}
interface UpdateMultiSelectAction {
  payload: StringFieldValues;
  type: 'updateMultiSelectFieldValue';
}
interface UpdateNumberAction {
  payload: NumberFieldValue;
  type: 'updateNumberFieldValue';
}
interface UpdateNameAction {
  payload: NameFieldValue;
  type: 'updateNameFieldValue';
}
interface UpdatePhoneAction {
  payload: StringFieldValue;
  type: 'updatePhoneFieldValue';
}
interface UpdateRadioAction {
  payload: StringFieldValue;
  type: 'updateRadioFieldValue';
}
interface UpdateSelectAction {
  payload: StringFieldValue;
  type: 'updateSelectFieldValue';
}
interface UpdateTextAreaAction {
  payload: StringFieldValue;
  type: 'updateTextAreaFieldValue';
}
interface UpdateTextFieldAction {
  payload: StringFieldValue;
  type: 'updateTextFieldValue';
}
interface UpdateTimeAction {
  payload: StringFieldValue;
  type: 'updateTimeFieldValue';
}
interface UpdateWebsiteAction {
  payload: StringFieldValue;
  type: 'updateWebsiteFieldValue';
}

type GravityFormsContextAction =
  | InitializeFieldsAction
  | UpdateAddressAction
  | UpdateCaptchaFieldValueAction
  | UpdateCheckboxAction
  | UpdateDateFieldValueAction
  | UpdateEmailAction
  | UpdateErrorsAction
  | UpdateHiddenAction
  | UpdateMultiSelectAction
  | UpdateNameAction
  | UpdateNumberAction
  | UpdatePhoneAction
  | UpdateRadioAction
  | UpdateSelectAction
  | UpdateTextAreaAction
  | UpdateTextFieldAction
  | UpdateTimeAction
  | UpdateWebsiteAction;
export type GravityFormsDispatch = Dispatch<GravityFormsContextAction>;

// Local Variables
const initialState: GravityFormsState = {
  errors: {},
  fields: {
    addressFields: {},
    captchaFields: {},
    checkboxFields: {},
    dateFields: {},
    emailFields: {},
    hiddenFields: {},
    multiSelectFields: {},
    nameFields: {},
    numberFields: {},
    phoneFields: {},
    radioFields: {},
    selectFields: {},
    textAreaFields: {},
    textFields: {},
    timeFields: {},
    websiteFields: {},
  },
  initialized: false,
};

export const GravityFormsStateContext = createContext(initialState);
export const GravityFormsDispatchContext
  = createContext<GravityFormsDispatch>(undefined as unknown as GravityFormsDispatch);

GravityFormsStateContext.displayName = 'GravityFormsState';
GravityFormsDispatchContext.displayName = 'GravityFormsDispatch';

const reducer: Reducer<GravityFormsState, GravityFormsContextAction> = (state, action) => {
  switch (action.type) {
    case 'initializeFields': {
      return {
        ...state,
        ...action.payload.state,
        initialized: true,
      };
    }
    case 'updateAddressFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          addressFields: {
            ...state.fields.addressFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateCaptchaFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          captchaFields: {
            ...state.fields.captchaFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateCheckboxFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          checkboxFields: {
            ...state.fields.checkboxFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateDateFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          dateFields: {
            ...state.fields.dateFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateEmailFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          emailFields: {
            ...state.fields.emailFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateHiddenFieldValue': {
      return {
        ...state,
        fields: {
          ...state.fields,
          hiddenFields: {
            ...state.fields.hiddenFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateErrors': {
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload.errors,
        },
      };
    }
    case 'updateMultiSelectFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          multiSelectFields: {
            ...state.fields.multiSelectFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateNameFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          nameFields: {
            ...state.fields.nameFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateNumberFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          numberFields: {
            ...state.fields.numberFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updatePhoneFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          phoneFields: {
            ...state.fields.phoneFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateRadioFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          radioFields: {
            ...state.fields.radioFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateSelectFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          selectFields: {
            ...state.fields.selectFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateTextAreaFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          textAreaFields: {
            ...state.fields.textAreaFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateTextFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          textFields: {
            ...state.fields.textFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateTimeFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          timeFields: {
            ...state.fields.timeFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    case 'updateWebsiteFieldValue': {
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.id]: null,
        },
        fields: {
          ...state.fields,
          websiteFields: {
            ...state.fields.websiteFields,
            [action.payload.id]: action.payload,
          },
        },
      };
    }
    default:
      throw new Error();
  }
};

const GravityFormsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <GravityFormsStateContext.Provider value={state}>
      <GravityFormsDispatchContext.Provider value={dispatch}>
        {children}
      </GravityFormsDispatchContext.Provider>
    </GravityFormsStateContext.Provider>
  );
};

export default GravityFormsContextProvider;
